import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import moment from 'moment';
import useAppointment from 'hooks/useAppointment';
import { useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  CANCEL_REASON_CODE,
  CANCEL_REASON_CODE_WITH_DESCRIPTION,
} from 'utils/constants';
import { loadAuthToken } from 'utils/storage';
import { ReactComponent as EmptyBucket } from 'assets/icons/empty-bucket.svg';
import { groupBy } from 'lodash';
import { useUser } from 'context/userContext';
import {
  calculateBackgroundColors,
  centerTextPlugin,
  optionConfig,
} from './utils';

ChartJS.register(ArcElement, Tooltip, Legend);

interface TableProps {
  clinicIds: string[];
  dateRange: { fromDate: string; toDate: string };
  shouldRun: boolean;
  cancelReasonCode?: string;
  onClickCancelReasonCode: (code: string | undefined) => void;
}

const DoughnutChart = ({
  clinicIds,
  dateRange,
  shouldRun,
  cancelReasonCode,
  onClickCancelReasonCode,
}: TableProps) => {
  const {
    user: { clinicUserRoles },
  } = useUser();

  const [selectedIndexByClick, setSelectedIndexByClick] = useState<
    number | null
  >(null);

  const [selectedIndexByHover, setSelectedIndexByHover] = useState<
    number | null
  >(null);

  const { data } = useAppointment({
    startDate: moment(dateRange.fromDate, 'YYYY-MM-DD')
      .startOf('days')
      .utc(true)
      .format(),
    endDate: moment(dateRange.toDate, 'YYYY-MM-DD')
      .endOf('days')
      .utc(true)
      .format(),
    limit: null,
    page: null,
    clinicIds,
    status: 'cancelled',
    // THIS IS TO BYPASS THE INTERCEPTOR
    axiosRequestConfig: {
      headers: {
        Authorization: `JWT ${loadAuthToken()?.accessToken}`,
      },
    },
    shouldRun,
  });

  const rejectedApptAmountByCancelReasonCode = useMemo(() => {
    if (data?.data) {
      const appointments = data.data;

      const groupByCancelReasonCode = groupBy(appointments, 'cancelReasonCode');

      const totalByGroupCode = Object.values(CANCEL_REASON_CODE).reduce(
        (accum: any, cancelReasonCode: string) => {
          const total = groupByCancelReasonCode[cancelReasonCode]?.length || 0;

          accum.push(total);

          return accum;
        },
        []
      );

      return totalByGroupCode;
    }

    return [];
  }, [data]);

  const chartTitle = useMemo(() => {
    if (clinicUserRoles.length === clinicIds.length) {
      return 'All practices';
    } else {
      const selectedPractices = clinicUserRoles.filter((item) =>
        clinicIds.includes(item.clinic.id)
      );

      return selectedPractices.map((item) => item.clinic.name).join(', ');
    }
  }, [clinicIds, clinicUserRoles]);

  const renderNoAppointments = () => {
    return (
      <div className="w-full h-[24rem] flex flex-col gap-y-1.6 justify-center items-center">
        <EmptyBucket />
        {clinicIds.length === 0 ? (
          <span>No selected practices</span>
        ) : (
          <span>No appointment available on this list</span>
        )}
      </div>
    );
  };

  const apptAmount = useMemo(() => {
    if (data?.data?.length !== 0 && cancelReasonCode) {
      const appts = data?.data || [];

      const amount = appts.filter(
        (item: any) => item.cancelReasonCode === cancelReasonCode
      ).length;

      return amount;
    } else {
      return data?.data.length || 0;
    }
  }, [data?.data, cancelReasonCode]);

  const renderChart = () => {
    return (
      <>
        <div className="mr-8 flex flex-col items-center">
          <>
            <h5 className="text-16 text-dim-grey text-center font-bold mb-[3rem]">
              {chartTitle}
            </h5>
            <div
              onMouseLeave={() => {
                setTimeout(() => {
                  setSelectedIndexByHover(null);
                }, 100);
              }}
            >
              <Doughnut
                width={400}
                plugins={[centerTextPlugin()]}
                data={{
                  labels: CANCEL_REASON_CODE_WITH_DESCRIPTION.map(
                    (item) => item.description
                  ),
                  datasets: [
                    {
                      label: 'Appts',
                      borderColor: ['#fff'],
                      borderWidth: 1,
                      data: rejectedApptAmountByCancelReasonCode.map(
                        (value: number) => (value > 0 ? Math.log(value + 1) : 0)
                      ),
                      ...calculateBackgroundColors({
                        selectedIndexByClick,
                        selectedIndexByHover,
                      }),
                    },
                  ],
                }}
                options={{
                  ...optionConfig,
                  plugins: {
                    ...optionConfig.plugins,
                    //@ts-ignore
                    centerText: { text: apptAmount },
                    tooltip: {
                      ...optionConfig.plugins.tooltip,
                      callbacks: {
                        label: (context: any) => {
                          const rawValue = context.raw as any;

                          const value =
                            rawValue > 0 ? Math.exp(rawValue) - 1 : 0;

                          const percentage =
                            data?.data?.length !== 0
                              ? (value / data.data.length) * 100
                              : 0;

                          return `Appts: ${parseInt(
                            value as any,
                            10
                          ).toLocaleString('en-US')} ${
                            percentage !== 0
                              ? `(${percentage.toFixed(2)}%)`
                              : ''
                          }`;
                        },
                      },
                    },
                  },
                  onHover: (_, elements) => {
                    if (elements.length > 0) {
                      const index = elements[0].index as number;
                      setSelectedIndexByHover(index);
                    } else {
                      setSelectedIndexByHover(null);
                    }
                  },
                  onClick: (_, elements) => {
                    if (elements.length > 0) {
                      const index = elements[0].index as number;
                      if (selectedIndexByClick === index) {
                        setSelectedIndexByClick(null);
                        onClickCancelReasonCode(undefined);
                      } else {
                        setSelectedIndexByClick(index);
                        onClickCancelReasonCode(
                          CANCEL_REASON_CODE_WITH_DESCRIPTION[index].code
                        );
                      }
                    } else {
                      setSelectedIndexByClick(null);
                      onClickCancelReasonCode(undefined);
                    }
                  },
                }}
              />
            </div>
          </>
        </div>
        <div className="p-2.4 rounded-[1rem] bg-[#F9F9F9] border-1 border-light-grey">
          {CANCEL_REASON_CODE_WITH_DESCRIPTION.map((item) => {
            return (
              <div key={item.code} className="flex flex-row">
                <div
                  className="w-[2rem] h-[2rem] mr-[1.2rem] mb-[2rem]"
                  style={{
                    background: item.color,
                  }}
                />
                <div>{item.description}</div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <>
      <h5 className="text-16 font-bold mb-[2.4rem]">Reasons for rejection</h5>
      <div className="flex flex-row justify-center items-center  bg-white shadow-primary rounded-[1rem] p-3.2">
        {data?.metadata.total === 0 ? renderNoAppointments() : renderChart()}
      </div>
    </>
  );
};

export default DoughnutChart;
